import {inject, Injectable} from '@angular/core';
import {SuperObjectModel} from './definitions/super-object-model';
import {Section, SectionsContainer} from './definitions/sections-container';
import {UserCacheService} from './user-cache.service';
import {FieldParameters} from "./definitions/field-parameters";
import {MetaField} from "./definitions/meta-field";
import {IfType} from "./definitions/field-if";
import {FieldConditionService} from "./field-condition.service";

@Injectable({
  providedIn: 'root'
})
export class ModelSectionsService {
  // Injections
  private readonly userCacheService: UserCacheService = inject(UserCacheService);
  private readonly fieldCondition: FieldConditionService = inject(FieldConditionService);

  async getCreateSectionsContainer(
    rootObject: SuperObjectModel, templateGroupId?: string): Promise<SectionsContainer> {
    const sectionsContainer = new SectionsContainer();
    const allSections = await this.getObjectSections(rootObject, templateGroupId);
    sectionsContainer.sections = this.filterSectionsByConditions(allSections, rootObject);
    sectionsContainer.rootObject = rootObject;
    sectionsContainer.templateGroupId = templateGroupId;
    return sectionsContainer;
  }

  private filterSectionsByConditions(sections: Section[], rootObject: SuperObjectModel): Section[] {
    return sections.filter(section => {
      if (!section.conditions_id) return true;

      // Use existing condition logic to evaluate if a section should be displayed
      const fieldParameters = new FieldParameters();
      fieldParameters.object = rootObject;
      fieldParameters.rootObject = rootObject;
      fieldParameters.field = {conditions_id: section.conditions_id} as MetaField;
      fieldParameters.edit = false;

      const conditions = this.fieldCondition.getFieldConditions(fieldParameters.field);
      const showCondition = conditions?.find(c => c.if_type === IfType.SHOW);

      if (!showCondition) return true;

      return this.fieldCondition.runIfItem(showCondition, fieldParameters);
    });
  }

  nextError(sectionsContainer: SectionsContainer) {
    const errors = this.getSectionErrors(sectionsContainer);
    const errorId = errors[sectionsContainer.errorPointer];
    sectionsContainer.errorPointer++;
    if (sectionsContainer.errorPointer >= errors.length) {
      sectionsContainer.errorPointer = 0;
    }
    return errorId;
  }


  getSectionErrors(sectionsContainer: SectionsContainer): string[] {
    const errors = [];
    for (const fieldName in sectionsContainer.formGroup.controls) {
      if (sectionsContainer.formGroup.controls.hasOwnProperty(fieldName)) {
        const fieldCtrl = sectionsContainer.formGroup.controls[fieldName];
        if (fieldCtrl.invalid) {
          errors.push(fieldName);
        }
      }
    }
    return errors;
  }

  // Getting section information for the object.
  private async getObjectSections(object: SuperObjectModel, templateGroupId?): Promise<Section[]> {
    let res: Section[];
    const objectType = object.object_type;
    if (objectType) {
      res = object.template_sections;
      if (!res) {
        res = await this.userCacheService.getModelSections(objectType, templateGroupId);
      }
    }
    return res;
  }

}
